import { useState } from 'react'

function FilterSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section className="border-b border-mk-line py-4 first:pt-0 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex min-h-[32px] w-full items-center justify-between text-left text-[15px] text-m1-ink transition-colors hover:text-m1-violet focus:outline-none focus-visible:text-m1-violet"
      >
        {title}
        <i
          className={`ri-arrow-down-s-line text-xl text-m1-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && <div className="mt-3">{children}</div>}
    </section>
  )
}

function CheckRow({ label, count, checked, onChange }) {
  return (
    <label className="group flex min-h-[40px] cursor-pointer items-center gap-3 text-sm">
      <input type="checkbox" checked={checked} onChange={onChange} className="peer sr-only" />
      <span
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-mk-line-2 bg-mk-primary text-transparent transition-colors group-hover:border-mk-line-3 peer-checked:border-m1-violet peer-checked:bg-m1-violet peer-checked:text-m1-ground peer-focus-visible:ring-1 peer-focus-visible:ring-m1-violet"
        aria-hidden="true"
      >
        <i className="ri-check-line text-[13px] leading-none" />
      </span>
      <span
        className={`min-w-0 flex-1 truncate transition-colors ${checked ? 'text-m1-ink' : 'text-m1-ink-soft'} group-hover:text-m1-ink`}
      >
        {label}
      </span>
      <span className="font-m1-mono text-[12px] tabular-nums text-m1-muted">{count}</span>
    </label>
  )
}

/**
 * Left-hand filter rail for the My Cards collection: status, card type and alert flags,
 * in collapsible sections (marketplace-style).
 */
export default function CollectionFilters({
  statusOptions,
  statusFilter,
  onStatusChange,
  typeOptions,
  selectedTypes,
  onToggleType,
  alertOptions,
  selectedAlerts,
  onToggleAlert,
  canClear,
  onClear,
}) {
  return (
    <div>
      <FilterSection title="Status">
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((option) => {
            const active = statusFilter === option.key
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => onStatusChange(option.key)}
                aria-pressed={active}
                className={`flex h-8 items-center gap-2 rounded-full border px-3 text-sm transition-colors duration-200 focus:outline-none focus-visible:border-mk-line-3 ${
                  active
                    ? 'border-mk-line-2 bg-mk-primary font-medium text-m1-ink'
                    : 'border-mk-line text-mk-muted hover:border-mk-line-2 hover:text-m1-ink'
                }`}
              >
                {option.label}
                <span
                  className={`font-m1-mono text-[11px] tabular-nums ${active ? 'text-m1-ink' : 'text-mk-muted'}`}
                >
                  {option.count}
                </span>
              </button>
            )
          })}
        </div>
      </FilterSection>

      {typeOptions.length > 0 && (
        <FilterSection title="Card type">
          <div>
            {typeOptions.map((option) => (
              <CheckRow
                key={option.key}
                label={option.label}
                count={option.count}
                checked={selectedTypes.includes(option.key)}
                onChange={() => onToggleType(option.key)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {alertOptions.length > 0 && (
        <FilterSection title="Attention">
          <div>
            {alertOptions.map((option) => (
              <CheckRow
                key={option.key}
                label={option.label}
                count={option.count}
                checked={selectedAlerts.includes(option.key)}
                onChange={() => onToggleAlert(option.key)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {canClear && (
        <button
          type="button"
          onClick={onClear}
          className="mt-4 min-h-[40px] text-[13px] tracking-[0.04em] text-m1-muted underline-offset-4 transition-colors hover:text-m1-violet hover:underline"
        >
          Clear all filters
        </button>
      )}
    </div>
  )
}
